/**
 * CoachLine — the one merged coach voice inside the loop hero
 * (direction §1, decision 4). Priority: a proactive trigger if one fired
 * this load, else the `useMotivation` daily line. This merges the
 * standalone motivation card into the hero (the ChampionshipCard race
 * motivation stays for the race card; this is the primary daily voice).
 *
 * On the completion beat the line cross-fades to an acknowledgment
 * (`That's N in a row …`) — the coach is the voice of the reward
 * (direction §5). Reduced motion keeps the swap, drops the fade.
 *
 * Hides cleanly (renders null) when there is no line and no
 * acknowledgment — same contract as the motivation block it replaces
 * (no placeholder while the LLM line is in flight).
 */
import type { ProactiveTriggerType } from '../../lib/proactiveCopy';
import { proactiveTypeReadable } from '../../lib/proactiveKicker';
import { useReactionTapActivation } from '../../hooks/useReactionTapActivation';
import { parseRunIdFromCoachHref } from '../../lib/run/runContextActivation';
import styles from './CoachLine.module.css';

export interface CoachLineProps {
  /** Coach display name (the daily voice). */
  coachName?: string;
  /** The daily motivation line (`useMotivation().data?.message`). */
  line: string | null;
  /** Tap target — the coach chat. */
  href?: string;
  /** Primary proactive trigger this load (`useProactiveChecks`). When set
   *  AND a line exists, the line renders as a `--today-soft` banner with a
   *  "RELATED · {trigger}" kicker. */
  proactiveType?: ProactiveTriggerType | null;
  /** Completion beat — when true with `acknowledgment`, the line swaps to
   *  the acknowledgment with a cross-fade. */
  celebrating?: boolean;
  /** The reward acknowledgment shown during the beat. */
  acknowledgment?: string | null;
  /**
   * W1-TODAY-HERO (TH-12, Q2) — `'v2'` is the interim 2.0 coach line: event-driven
   * only, after the action area. It renders the post-run reaction (the href
   * carries `?run=`) or the proactive "RELATED" line, and nothing else: no daily
   * motivation line, no resting invitation, no "Ask ›", no streak acknowledgment.
   * Absent = the legacy line, byte for byte.
   */
  variant?: 'v2';
}

export function CoachLine({
  coachName = 'Airo',
  line,
  href = '/v2/chat',
  proactiveType,
  celebrating,
  acknowledgment,
  variant,
}: CoachLineProps) {
  const showAck = !!celebrating && !!acknowledgment;
  const displayText = showAck ? acknowledgment : line;

  // Rung-2 §2.1 — when this line is the reaction echo (href carries ?run=)
  // and coach_convo_v1 is ON, the tap mints the activation nonce and
  // navigates in-app. Otherwise undefined → plain anchor, byte-identical.
  const reactionTap = useReactionTapActivation(href);

  if (variant === 'v2') {
    // The same two tests the legacy branch below makes (REL-13): the echo is
    // the line whose href carries a run; a proactive line is a non-echo line
    // with a readable trigger. Anything else is the generic daily line, which
    // the 2.0 hero does not show.
    const isEcho = parseRunIdFromCoachHref(href) !== null;
    const related = proactiveTypeReadable(proactiveType ?? null);
    const isProactive = !isEcho && !!related;
    if (!line || (!isEcho && !isProactive)) return null;
    const v2Class = [styles.coachLine, styles.v2, isProactive ? styles.isProactive : '']
      .filter(Boolean)
      .join(' ');
    return (
      <a className={v2Class} href={href} onClick={reactionTap}>
        {!isProactive && <span className={styles.dot} aria-hidden />}
        <span className={styles.txt}>
          {isProactive && <span className={styles.v2Kick}>RELATED · {related}</span>}
          {!isProactive && <span className={styles.who}>{coachName}</span>}
          {!isProactive && ' · '}
          {line}
        </span>
      </a>
    );
  }

  // Durable resting state (design pass 2026-06-19) — Today is never
  // coach-silent. With no proactive trigger and no daily motivation line, show
  // a quiet invitation on the coach hue instead of rendering nothing. The FAB
  // is the floor; this woven-in line is the ceiling. Closes the original bug
  // where an empty CoachLine left no in-content path to the coaches.
  if (!displayText) {
    return (
      <a className={`${styles.coachLine} ${styles.resting}`} href={href} onClick={reactionTap}>
        <span className={styles.txt}>
          <span className={styles.restKick}>YOUR COACH</span>
          {coachName}&rsquo;s here whenever you want to talk through today.{' '}
          <span className={styles.go}>Ask &rsaquo;</span>
        </span>
      </a>
    );
  }

  const relatedLabel = proactiveTypeReadable(proactiveType ?? null);

  // ⚖️ REL-13 (2026-08-16) — THE KICKER MUST NOT SURVIVE THE SWAP.
  //
  // Before the ruling, `proactiveType` and the echo could never coexist: the
  // gate in today.tsx nulled the echo whenever any trigger fired, so a kicker
  // implied a proactive LINE. Now a chore can be detected while the post-run
  // note wins the slot, and `proactiveType` still threads down here — it also
  // feeds the ChampionshipCard's race-motivation kicker, where the trigger IS
  // the subject and the kicker is correct. Left alone, this line would render
  // the athlete's post-run note under "RELATED · Draft next week": a label
  // about the losing message, attached to the winning one.
  //
  // Detected from the href rather than a new prop, because the href already
  // carries the answer and cannot disagree with itself: `?run=` is present if
  // and only if this line IS the echo (`heroCoachHref` is the reaction's own
  // href or a bare `/v2/chat`). A parallel `isEcho` prop could drift from the
  // href, and the href is what the tap uses.
  const isEcho = parseRunIdFromCoachHref(href) !== null;

  // During the beat the acknowledgment reads as a normal line, not the
  // proactive banner.
  const isProactive = !showAck && !isEcho && !!relatedLabel;

  const cls = [styles.coachLine, isProactive ? styles.isProactive : ''].filter(Boolean).join(' ');
  const txtCls = [styles.txt, showAck ? styles.fade : ''].filter(Boolean).join(' ');

  return (
    <a className={cls} href={href} onClick={reactionTap}>
      {!isProactive && <span className={styles.dot} aria-hidden />}
      <span className={txtCls}>
        {isProactive && <span className={styles.kick}>RELATED · {relatedLabel}</span>}
        {!isProactive && <span className={styles.who}>{coachName}</span>}
        {!isProactive && ' · '}
        {displayText} <span className={styles.go}>Ask ›</span>
      </span>
    </a>
  );
}
